/*
 * VipMembershipHero — Represent-style split hero for /vip.
 * LEFT: gold, shiny, slowly-rotating membership card with the Boscage deer
 * logo on the front and "VIP · MEMBER" + benefits on the back (leather grain).
 * RIGHT: 2-step details form (name → email) → "Create Membership".
 * BACKGROUND: dark grey with a subtle animated twist gradient.
 */

import { useEffect, useState } from "react";

const SILVER_CARD_IMAGE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-jackets-6/artifacts/hpmjg460_ChatGPT%20Image%20Aug%2029%2C%202026%2C%2007_12_02%20PM.png";

export default function VipMembershipHero({
  vipTicket,
  onSubmit,        // (name, email) => Promise
  submitting,
  onThankYou,      // callback to show thank-you popup after success
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    const fullName = `${firstName} ${lastName}`.trim();
    if (!fullName || !email) return;
    await onSubmit?.(fullName, email);
    onThankYou?.();
  };

  return (
    <section
      data-testid="vip-membership-hero"
      className="relative bg-[#0a0a0a] text-white overflow-hidden isolate"
    >
      {/* Animated twist background */}
      <div aria-hidden="true" className="absolute inset-0 vip-motion-bg" />
      {/* Subtle vignette on top */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50" />

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
        {/* LEFT — silver Boscage member card, slowly rotating on Y-axis (front + plain back) */}
        <div className="flex justify-center lg:justify-start">
          <div className="vip-card-rotator" data-testid="vip-gold-card">
            <div className="vip-card-3d">
              {/* FRONT face — top half of source image (Member + BOSCAGE + logo) */}
              <div className="vip-card-face vip-card-face--front" data-testid="vip-card-logo" />
              {/* BACK face — bottom half of source image (signature + WEAR THE HERITAGE) */}
              <div className="vip-card-face vip-card-face--back" aria-hidden="true" />
            </div>
            {vipTicket && (
              <div className="mt-6 text-center">
                <span className="text-[10px] tracking-[0.32em] uppercase text-white/70">
                  Ticket &nbsp;·&nbsp;
                </span>
                <span className="font-serif text-[13px] text-[#c9a84c] tracking-widest">
                  {vipTicket}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — details form */}
        <div className="max-w-md w-full mx-auto lg:mx-0 lg:ml-auto">
          <p className="text-[11px] tracking-[0.22em] uppercase text-white/60">
            1 / 2 &nbsp; Details
          </p>
          <form onSubmit={submit} className="mt-6 space-y-6" data-testid="vip-hero-form">
            <FieldDark
              testId="vip-hero-email"
              label="Email"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={setEmail}
              required
            />
            <FieldDark
              testId="vip-hero-first-name"
              label="First name"
              placeholder="Your first name"
              value={firstName}
              onChange={setFirstName}
              required
            />
            <FieldDark
              testId="vip-hero-last-name"
              label="Last name"
              placeholder="Your last name"
              value={lastName}
              onChange={setLastName}
              required
            />

            <label className="flex items-start gap-3 pt-2 text-[13px] text-white/75 leading-relaxed">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="vip-hero-subscribe-check"
                className="mt-1 accent-[#c9a84c]"
              />
              <span>
                Subscribe to receive email updates about Boscage product
                launches, promotions and exclusive discounts.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="vip-hero-submit"
              className="mt-2 w-full bg-white text-black py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#c9a84c] transition-colors disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Create Membership"}
            </button>

            <p className="text-center text-[11px] tracking-[0.14em] text-white/55 mt-2">
              Already a VIP?{" "}
              <a href="/login" className="underline hover:text-white">Login here</a>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

const FieldDark = ({ testId, label, value, onChange, ...rest }) => (
  <div className="border-b border-white/25 focus-within:border-[#c9a84c] pb-2 transition-colors">
    <label className="block text-[13px] text-white/85 mb-1">{label}</label>
    <input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent text-white placeholder-white/40 text-sm focus:outline-none"
      {...rest}
    />
  </div>
);
